import { isScrollableElement } from "../utilities";

// Helper to legacy components
// (Action menu) works only with alignments instead of entire placement name
export const alignments = {
    start: "start",
    end: "end",
};

export const placements = {
    topStart: "top-start",
    top: "top",
    topEnd: "top-end",
    rightStart: "right-start",
    right: "right",
    rightEnd: "right-end",
    bottomStart: "bottom-start",
    bottom: "bottom",
    bottomEnd: "bottom-end",
    leftStart: "left-start",
    left: "left",
    leftEnd: "left-end",
};

/**
 * Helper to generate a placementFallback array based on a placement
 * first flipping to the same axes that the p (placement) and the top/left order (due to RTL assumptions)
 * Example1: if p (placement) is top, it will generate top/bottom/right/left
 * Example2: if p (placement) is left, it will generate left/right/top/bottom
 * @param {string} p - placemment
 * @returns {Array}
 */
export const getPlacementFallback = (p) => {
    const rightPlacements = Object.values(placements).filter((i) => i.includes("right"));
    const topPlacements = Object.values(placements).filter((i) => i.includes("top"));
    const bottomPlacements = Object.values(placements).filter((i) => i.includes("bottom"));
    const leftPlacements = Object.values(placements).filter((i) => i.includes("left"));

    if (p.includes(placements.top))
        return topPlacements.concat(bottomPlacements, rightPlacements, leftPlacements);
    if (p.includes(placements.bottom))
        return bottomPlacements.concat(topPlacements, rightPlacements, leftPlacements);
    if (p.includes(placements.left))
        return leftPlacements.concat(rightPlacements, topPlacements, bottomPlacements);
    if (p.includes(placements.right))
        return rightPlacements.concat(leftPlacements, topPlacements, bottomPlacements);
};

/**
 * @param {Object} config
 * @param {HTMLElement} config.trigger - Trigger element
 * @param {HTMLElement} config.target - Target element
 * @param {String} config.placement - One of the 12 available placements
 * @param {Array} config.placementFallback - Array containing the order of placements to work as a fallback when flip is active
 * @param {Boolean} config.flip - Defines when the flip feature is active
 * @param {Object} config.offsets - Define the distance between the trigger and the target in both axes
 * @param {Number} config.offsets.x - Distance between the trigger and the target on x axes
 * @param {Number} config.offsets.y - Distance between the trigger and the target on y axes
 * @param {Object} config.gutters - Define the gutter between the target the edges of the screen in both axes
 * @param {Number} config.gutters.x - Distance between the target and the edge of the screen on x axes
 * @param {Number} config.gutters.y - Distance between the target and the edge of the screen on y axes
 * @param {Number} config.pointer - Pointer size in pixels (if 0 the pointer will not be calculated)
 * @param {Boolean} config.adjustToEdge - Define if after placement target and pointer will be adjusted to edges of screen if needed
 * @param {HTMLElement} config._document - Document element
 * @param {HTMLElement} config._window - Window element
 * @returns
 */
export function getPlacement({
    trigger,
    target,
    placement,
    placementFallback = [],
    flip = false,
    offsets = { x: 0, y: 0 },
    gutters = { x: 0, y: 0 },
    pointer = 0,
    adjustToEdge = false,
    _document = document,
    _window = window,
}) {
    // Used for calculations when the target is in a relative/absolute/fixed/sticky parent.
    const triggerOffsetParent = trigger.offsetParent;
    const targetOffsetParent = target.offsetParent;

    const isTargetInOffsetParent = targetOffsetParent !== _document.body;
    const isTriggerInOffsetParent = triggerOffsetParent !== _document.body;

    let isTriggerInScrollableOffsetParent = false;

    // We only need to take in consideration scrolling, if the scrolling is different than body, if the target is in body everything works
    if (isTriggerInOffsetParent)
        isTriggerInScrollableOffsetParent = isScrollableElement(triggerOffsetParent);

    // To parent to use when calculating positions
    const triggerOffsetParentRect = isTriggerInOffsetParent
        ? triggerOffsetParent.getBoundingClientRect()
        : { top: 0, left: 0 }; // Stubbed values for document body

    // To parent to use when calculating positions
    const targetOffsetParentRect = isTargetInOffsetParent
        ? targetOffsetParent.getBoundingClientRect()
        : { top: 0, left: 0 }; // Stubbed values for document body

    // scroll offsets to use when calculating positions
    const scroll = isTargetInOffsetParent
        ? {
              x: parseInt(targetOffsetParent.scrollLeft),
              y: parseInt(targetOffsetParent.scrollTop),
          }
        : { x: _window.scrollX, y: _window.scrollY };

    // Trigger positions and deminsions
    const triggerRect = trigger.getBoundingClientRect();
    const triggerTop = parseInt(triggerRect.top);
    const triggerLeft = parseInt(triggerRect.left);
    const triggerBottom = parseInt(triggerRect.bottom);
    const triggerRight = parseInt(triggerRect.right);
    const triggerWidth = parseInt(triggerRect.width);
    const triggerHeight = parseInt(triggerRect.height);
    const triggerHalfWidth = parseInt(triggerWidth / 2);
    const triggerHalfHeight = parseInt(triggerHeight / 2);
    const triggerTopRelativeToTarget = parseInt(triggerRect.top - targetOffsetParentRect.top);
    const triggerLeftRelativeToTarget = parseInt(triggerRect.left - targetOffsetParentRect.left);
    const triggerBottomRelativeToTarget = parseInt(triggerRect.bottom - targetOffsetParentRect.top);
    const triggerRightRelativeToTarget = parseInt(triggerRect.right - targetOffsetParentRect.left);

    // Target deminsions
    const targetRect = target.getBoundingClientRect();
    const targetHeight = parseInt(targetRect.height);
    const targetWidth = parseInt(targetRect.width);
    const targetHalfWidth = parseInt(targetWidth / 2);
    const targetHalfHeight = parseInt(targetHeight / 2);

    // Pointer dimensions
    // Usually to create a pointer users add a square and rotate it, the size of the pointer turns into diagonal
    // and square diagonal is bigger than his side size, so we are adding a pointerRatio variable to adjust those cases
    const pointerHalfWidth = pointer / 2;
    const pointerRatio = 0.25;
    const pointerRatioSize = pointer * pointerRatio;
    const pointerWithRatio = pointer + pointerRatioSize;

    // Available spaces
    // The target will always be positioned around the trigger
    // so we find all the available spaces available around the trigger
    // When the trigger is inside a scrollable and relative parent (isTriggerInScrollableOffsetParent)
    // the calculations are slightly different
    const availableWidth = isTriggerInScrollableOffsetParent
        ? triggerOffsetParentRect.width
        : _window.innerWidth;

    const availableSpaceTop = isTriggerInScrollableOffsetParent
        ? triggerTop - triggerOffsetParentRect.top
        : triggerTop;

    const availableSpaceBottom = isTriggerInScrollableOffsetParent
        ? triggerOffsetParentRect.bottom - triggerBottom
        : _window.innerHeight - triggerBottom;

    const availableSpaceLeft = isTriggerInScrollableOffsetParent
        ? triggerLeft - triggerOffsetParentRect.left
        : triggerLeft;

    const availableSpaceRight = isTriggerInScrollableOffsetParent
        ? triggerOffsetParentRect.right - triggerRight
        : _document.body.clientWidth - triggerRight;

    const availableSpaceTopFromEnd = availableSpaceTop + triggerHeight;
    const availableSpaceTopFromMiddle = availableSpaceTop + triggerHalfHeight;
    const availableSpaceBottomFromStart = availableSpaceBottom + triggerHeight;
    const availableSpaceBottomFromMiddle = availableSpaceBottom + triggerHalfHeight;
    const availableSpaceLeftFromEnd = availableSpaceLeft + triggerWidth;
    const availableSpaceLeftFromMiddle = availableSpaceLeft + triggerHalfWidth;
    const availableSpaceRightFromStart = availableSpaceRight + triggerWidth;
    const availableSpaceRightFromMiddle = availableSpaceRight + triggerHalfWidth;

    // States
    // Will be updated during the calculations
    let newPlacement = placement;
    const position = { left: 0, top: 0 };
    const pointerPosition = { left: 0, top: 0 };

    // Fit
    // Check if the target fit into a available space
    const fitWidth = targetWidth + gutters.x < availableWidth;
    const fitTop = targetHeight + gutters.y < availableSpaceTop;
    const fitTopFromMiddle = targetHalfHeight + gutters.y < availableSpaceTopFromMiddle;
    const fitTopFromEnd = targetHeight + gutters.y < availableSpaceTopFromEnd;
    const fitBottom = targetHeight + gutters.y < availableSpaceBottom;
    const fitBottomFromMiddle = targetHalfHeight + gutters.y < availableSpaceBottomFromMiddle;
    const fitBottomFromStart = targetHeight + gutters.y < availableSpaceBottomFromStart;
    const fitLeft = targetWidth + gutters.x < availableSpaceLeft;
    const fitLeftFromMiddle = targetHalfWidth + gutters.x < availableSpaceLeftFromMiddle;
    const fitLeftFromEnd = targetWidth + gutters.x < availableSpaceLeftFromEnd;
    const fitRight = targetWidth + gutters.x < availableSpaceRight;
    const fitRightFromMiddle = targetHalfWidth + gutters.x < availableSpaceRightFromMiddle;
    const fitRightFromStart = targetWidth + gutters.x < availableSpaceRightFromStart;

    /**
     * Helper function that verifies if the target fit the placement in ALL the EDGES of the container
     * @param {string} p - placement
     * @returns {Boolean}
     */
    const fitsPlacement = (p) => {
        if (p === placements.top) return fitTop && fitLeftFromMiddle && fitRightFromMiddle;
        if (p === placements.bottom) return fitBottom && fitLeftFromMiddle && fitRightFromMiddle;
        if (p === placements.left) return fitLeft && fitTopFromMiddle && fitBottomFromMiddle;
        if (p === placements.right) return fitRight && fitTopFromMiddle && fitBottomFromMiddle;
        if (p === placements.topStart) return fitTop && fitRightFromStart && availableSpaceLeft > 0;
        if (p === placements.bottomStart)
            return fitBottom && fitRightFromStart && availableSpaceLeft > 0;
        if (p === placements.topEnd) return fitTop && fitLeftFromEnd && availableSpaceRight > 0;
        if (p === placements.bottomEnd)
            return fitBottom && fitLeftFromEnd && availableSpaceRight > 0;
        if (p === placements.leftStart)
            return fitLeft && fitBottomFromStart && availableSpaceTop > 0;
        if (p === placements.rightStart)
            return fitRight && fitBottomFromStart && availableSpaceTop > 0;
        if (p === placements.leftEnd) return fitLeft && fitTopFromEnd && availableSpaceBottom > 0;
        if (p === placements.rightEnd) return fitRight && fitTopFromEnd && availableSpaceBottom > 0;
    };

    // Base

    const setBaseTop = () => {
        position.top = triggerTopRelativeToTarget - targetHeight - offsets.y + scroll.y;
    };

    const setBaseBottom = () => {
        position.top = triggerBottomRelativeToTarget + offsets.y + scroll.y;
    };

    const setBaseLeft = () => {
        position.left = triggerLeftRelativeToTarget - targetWidth - offsets.x + scroll.x;
    };

    const setBaseRight = () => {
        position.left = triggerRightRelativeToTarget + offsets.x + scroll.x;
    };

    /**
     * Set the positions top and left to adjust the target to the 4 base placements (top|bottom|left|right)
     * @param {string} p - placement
     */
    const setBase = (p) => {
        if (p.includes(placements.top)) setBaseTop();
        if (p.includes(placements.bottom)) setBaseBottom();
        if (p.includes(placements.left)) setBaseLeft();
        if (p.includes(placements.right)) setBaseRight();
    };

    // Alignment

    const setAlignmentHorizontal = () => {
        position.left = triggerLeftRelativeToTarget - (targetWidth - triggerWidth) / 2 + scroll.x;
    };

    const setAlignmentHorizontalStart = () => {
        position.left = triggerLeftRelativeToTarget + scroll.x;
    };

    const setAlignmentHorizontalEnd = () => {
        position.left = triggerRightRelativeToTarget - targetWidth + scroll.x;
    };

    const setAlignmentVertical = () => {
        position.top = triggerTopRelativeToTarget - (targetHeight - triggerHeight) / 2 + scroll.y;
    };

    const setAlignmentVerticalStart = () => {
        position.top = triggerTopRelativeToTarget + scroll.y;
    };

    const setAlignmentVerticalEnd = () => {
        position.top = triggerBottomRelativeToTarget - targetHeight + scroll.y;
    };

    /**
     * Set the positions top and left to adjust the target to middle(default)/start/end alignments
     * @param {string} p - placement
     */
    const setAlignment = (p) => {
        if (p === placements.top || p === placements.bottom) setAlignmentHorizontal();
        if (p === placements.left || p === placements.right) setAlignmentVertical();
        if (p === placements.topStart || p === placements.bottomStart)
            setAlignmentHorizontalStart();
        if (p === placements.topEnd || p === placements.bottomEnd) setAlignmentHorizontalEnd();
        if (p === placements.leftStart || p === placements.rightStart) setAlignmentVerticalStart();
        if (p === placements.leftEnd || p === placements.rightEnd) setAlignmentVerticalEnd();
    };

    // Flip

    const setFlipTop = () => {
        if (!fitTop && fitBottom)
            newPlacement = newPlacement.replace(placements.top, placements.bottom);
    };

    const setFlipBottom = () => {
        if (!fitBottom && fitTop)
            newPlacement = newPlacement.replace(placements.bottom, placements.top);
    };

    const setFlipLeft = () => {
        if (!fitLeft && fitRight)
            newPlacement = newPlacement.replace(placements.left, placements.right);
    };

    const setFlipRight = () => {
        if (!fitRight && fitLeft)
            newPlacement = newPlacement.replace(placements.right, placements.left);
    };

    /**
     * If the target doesn't fit on the placement and fit on the oposite set newPlacement to the oposite placement
     * @param {string} p - placement
     */
    const setFlip = (p) => {
        if (p.includes(placements.top)) setFlipTop();
        if (p.includes(placements.bottom)) setFlipBottom();
        if (p.includes(placements.left)) setFlipLeft();
        if (p.includes(placements.right)) setFlipRight();
    };

    // Pointer

    const setPointerTop = () => {
        pointerPosition.top = targetHeight - pointerHalfWidth;
    };

    const setPointerBottom = () => {
        pointerPosition.top = -pointerHalfWidth;
    };

    const setPointerLeft = () => {
        pointerPosition.left = targetWidth - pointerHalfWidth;
    };

    const setPointerRight = () => {
        pointerPosition.left = -pointerHalfWidth;
    };

    const setPointerHorizontal = () => {
        pointerPosition.left = targetHalfWidth - pointerHalfWidth;
    };

    const setPointerHorizontalStart = () => {
        pointerPosition.left = triggerHalfWidth - pointerHalfWidth;
    };

    const setPointerHorizontalEnd = () => {
        pointerPosition.left = targetWidth - triggerHalfWidth - pointerHalfWidth;
    };

    const setPointerVertical = () => {
        pointerPosition.top = targetHalfHeight - pointerHalfWidth;
    };

    const setPointerVerticalStart = () => {
        pointerPosition.top = triggerHalfHeight - pointerHalfWidth;
    };

    const setPointerVerticalEnd = () => {
        pointerPosition.top = targetHeight - triggerHalfHeight - pointerHalfWidth;
    };

    /**
     * Set the positions top and left to adjust the pointer regards the target placement
     * NOTE: In order to pointer works as expected it needs to be absolute and the target relative
     * @param {string} p - placement
     */
    const setPointer = (p) => {
        if (p.includes(placements.top)) setPointerTop();
        if (p.includes(placements.bottom)) setPointerBottom();
        if (p.includes(placements.left)) setPointerLeft();
        if (p.includes(placements.right)) setPointerRight();
        if (p === placements.top || p === placements.bottom) setPointerHorizontal();
        if (p === placements.left || p === placements.right) setPointerVertical();
        if (p === placements.topStart || p === placements.bottomStart) setPointerHorizontalStart();
        if (p === placements.topEnd || p === placements.bottomEnd) setPointerHorizontalEnd();
        if (p === placements.leftStart || p === placements.rightStart) setPointerVerticalStart();
        if (p === placements.leftEnd || p === placements.rightEnd) setPointerVerticalEnd();
    };

    // Adjust to Edge

    const setAdjustHorizontalFromMiddle = () => {
        const maxToExceed = targetHalfWidth + triggerHalfWidth - pointer;
        if (!fitLeftFromMiddle) {
            const exceededLeft = targetHalfWidth - availableSpaceLeftFromMiddle + gutters.x;
            position.left = position.left + Math.min(exceededLeft, maxToExceed);
            pointerPosition.left = Math.max(pointerRatioSize, pointerPosition.left - exceededLeft);
        } else if (!fitRightFromMiddle) {
            const exceededRight = targetHalfWidth - availableSpaceRightFromMiddle + gutters.x;
            position.left = position.left - Math.min(exceededRight, maxToExceed);
            pointerPosition.left = Math.min(
                targetWidth - pointerWithRatio,
                pointerPosition.left + exceededRight
            );
        }
    };

    const setAdjustHorizontalFromStart = () => {
        if (!fitRightFromStart) {
            const exceededRight = targetWidth - availableSpaceRightFromStart + gutters.x;
            const maxToExceed = targetWidth - pointer;
            position.left = position.left - Math.min(exceededRight, maxToExceed);
            pointerPosition.left = Math.min(
                targetWidth - pointerWithRatio,
                pointerPosition.left + exceededRight
            );
        } else if (availableSpaceLeft < 0) {
            const exceededLeft = Math.abs(availableSpaceLeft) + gutters.x;
            const maxToExceed = triggerWidth - pointer;
            position.left = position.left + Math.min(exceededLeft, maxToExceed);
            pointerPosition.left = Math.max(pointerRatioSize, pointerPosition.left - exceededLeft);
        }
    };

    const setAdjustHorizontalFromEnd = () => {
        if (!fitLeftFromEnd) {
            const exceededLeft = targetWidth - availableSpaceLeftFromEnd + gutters.x;
            const maxToExceed = targetWidth - pointer;
            position.left = position.left + Math.min(exceededLeft, maxToExceed);
            pointerPosition.left = Math.max(pointerRatioSize, pointerPosition.left - exceededLeft);
        } else if (availableSpaceRight < 0) {
            const exceededRight = Math.abs(availableSpaceRight) + gutters.x;
            const maxToExceed = triggerWidth - pointer;
            position.left = position.left - Math.min(exceededRight, maxToExceed);
            pointerPosition.left = Math.min(
                targetWidth - pointerWithRatio,
                pointerPosition.left + exceededRight
            );
        }
    };

    const setAdjustVerticalFromMiddle = () => {
        const maxToExceed = targetHalfHeight + triggerHalfHeight - pointer;
        if (!fitTopFromMiddle) {
            const exceededTop = targetHalfHeight - availableSpaceTopFromMiddle + gutters.y;
            position.top = position.top + Math.min(exceededTop, maxToExceed);
            pointerPosition.top = Math.max(pointerRatioSize, pointerPosition.top - exceededTop);
        } else if (!fitBottomFromMiddle) {
            const exceededBottom = targetHalfHeight - availableSpaceBottomFromMiddle + gutters.y;
            position.top = position.top - Math.min(exceededBottom, maxToExceed);
            pointerPosition.top = Math.min(
                targetHeight - pointerWithRatio,
                pointerPosition.top + exceededBottom
            );
        }
    };

    const setAdjustVerticalFromStart = () => {
        if (!fitBottomFromStart) {
            const exceededBottom = targetHeight - availableSpaceBottomFromStart + gutters.y;
            const maxToExceed = targetHeight - pointer;
            position.top = position.top - Math.min(exceededBottom, maxToExceed);
            pointerPosition.top = Math.min(
                targetHeight - pointerWithRatio,
                pointerPosition.top + exceededBottom
            );
        } else if (availableSpaceTop < 0) {
            const exceededTop = Math.abs(availableSpaceTop) + gutters.y;
            const maxToExceed = triggerHeight - pointer;
            position.top = position.top + Math.min(exceededTop, maxToExceed);
            pointerPosition.top = Math.max(pointerRatioSize, pointerPosition.top - exceededTop);
        }
    };
    const setAdjustVerticalFromEnd = () => {
        if (!fitTopFromEnd) {
            const exceededTop = targetHeight - availableSpaceTopFromEnd + gutters.y;
            const maxToExceed = targetHeight - pointer;
            position.top = position.top + Math.min(exceededTop, maxToExceed);
            pointerPosition.top = Math.max(pointerRatioSize, pointerPosition.top - exceededTop);
        } else if (availableSpaceBottom < 0) {
            const exceededBottom = Math.abs(availableSpaceBottom) + gutters.y;
            const maxToExceed = triggerHeight - pointer;
            position.top = position.top - Math.min(exceededBottom, maxToExceed);
            pointerPosition.top = Math.min(
                targetHeight - pointerWithRatio,
                pointerPosition.top + exceededBottom
            );
        }
    };

    /**
     * Before positioning base/alignment, if the placement is left/right and it doesn't fit on the left/tight edges
     * this function will check if the width fits the container and adjust to top/bottom instead
     * @param {string} p - placement
     */
    const setAdjustToEdgeBefore = (p) => {
        if (!fitWidth) return;

        if (
            (p.includes(placements.left) && !fitLeft) ||
            (p.includes(placements.right) && !fitRight)
        ) {
            newPlacement = fitTop ? placements.top : fitBottom ? placements.bottom : newPlacement;
        }
    };

    /**
     * After positioning base/alignment and flipping, if the target still doesn't fit on the container
     * this function will understand how much the target is exceeding the container
     * to pull or push the target and pointer in that direction
     * @param {string} p - placement
     */
    const setAdjustToEdgeAfter = (p) => {
        if (p === placements.top || p === placements.bottom) setAdjustHorizontalFromMiddle();
        if (p === placements.left || p === placements.right) setAdjustVerticalFromMiddle();
        if (p === placements.topStart || p === placements.bottomStart)
            setAdjustHorizontalFromStart();
        if (p === placements.topEnd || p === placements.bottomEnd) setAdjustHorizontalFromEnd();
        if (p === placements.leftStart || p === placements.rightStart) setAdjustVerticalFromStart();
        if (p === placements.leftEnd || p === placements.rightEnd) setAdjustVerticalFromEnd();
    };

    // Flip will change the newPlacement to an available placement if it exists otherwise it will keep the placement
    if (flip && placementFallback.length > 0) {
        let foundPlace = false;
        placementFallback.forEach((p) => {
            if (!foundPlace && fitsPlacement(p)) {
                newPlacement = p;
                foundPlace = true;
            }
        });
    } else if (flip) {
        setFlip(placement);
    }

    // If the newPlacement is left/right and it doesn't fit on the left/right its because we don't have flip or all the flip positions failed
    // in this case if fits the width of the container we will flip to top and bottom
    // NOTE: If flipping and placementFallback are well implemented this will only be a last resource before setting base and alignment
    if (adjustToEdge) setAdjustToEdgeBefore(newPlacement);

    // Sets the target position top|bottom|left|right based on newPlacement
    setBase(newPlacement);

    // Sets the target alignment middle(default)|start|end based on newPlacement
    setAlignment(newPlacement);

    // Sets the pointer base/alignment based on newPlacement
    if (pointer > 0) setPointer(newPlacement);

    // Set adjusts to edges based on newPlacement
    if (adjustToEdge) setAdjustToEdgeAfter(newPlacement);

    return { position, pointer: pointerPosition };
}
